/* ZILOX Studio. Native links/forms remain the fallback; no client-side fake success. */
(() => {
  'use strict';
  if (window.ZiloxStudio) { window.ZiloxStudio.refresh(); return; }
  const controllers = new Map();
  const all = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  function mount(el, setup) {
    if (controllers.has(el)) return;
    const abort = new AbortController();
    const on = (node, type, fn, options = {}) => node?.addEventListener(type, fn, {...options, signal: abort.signal});
    const dispose = setup(on) || (() => {});
    controllers.set(el, () => { abort.abort(); dispose(); });
  }
  function refresh(root = document) {
    all(root, '.zs-header').forEach(header => mount(header, () => {
      const measure = () => {
        const height = Math.ceil(header.getBoundingClientRect().height);
        if (!height) return;
        ['--zs-header', '--header-height', '--header-group-height', '--top-row-height'].forEach(key => document.documentElement.style.setProperty(key, `${height}px`));
      };
      measure();
      if (!window.ResizeObserver) return;
      const observer = new ResizeObserver(measure); observer.observe(header);
      return () => observer.disconnect();
    }));
    all(root, '[data-zs-menu]').forEach(menu => mount(menu, on => {
      const summary = menu.querySelector('summary');
      const label = menu.querySelector('[data-zs-menu-label]');
      const close = focus => { menu.open = false; if (focus) summary.focus(); };
      on(menu, 'toggle', () => { if (label) label.textContent = menu.open ? 'Close' : 'Menu'; });
      on(menu, 'keydown', e => { if (e.key === 'Escape' && menu.open) { e.preventDefault(); close(true); } });
      on(menu, 'focusout', () => requestAnimationFrame(() => { if (!menu.contains(document.activeElement)) close(false); }));
      menu.querySelectorAll('a').forEach(a => on(a, 'click', () => close(false)));
      on(document, 'click', e => { if (menu.open && !menu.contains(e.target)) close(false); });
      const mq = matchMedia('(min-width: 990px)');
      on(mq, 'change', () => { if (mq.matches) close(false); });
    }));
    all(root, '[data-zs-subnav]').forEach(nav => mount(nav, on => {
      const pairs = [...nav.querySelectorAll('a[href^="#"]')].map(a => ({a, section:document.getElementById(a.hash.slice(1))})).filter(p => p.section);
      let scheduled = false;
      const update = () => {
        scheduled = false;
        const cutoff = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zs-header')) + nav.offsetHeight + 48;
        let active = pairs[0];
        pairs.forEach(p => { if (p.section.getBoundingClientRect().top <= cutoff) active = p; });
        pairs.forEach(p => { if (p === active) p.a.setAttribute('aria-current','location'); else p.a.removeAttribute('aria-current'); });
      };
      const schedule = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(update); } };
      on(window, 'scroll', schedule, {passive:true}); on(window, 'resize', schedule, {passive:true}); update();
    }));
    all(root, '[data-zs-gallery]').forEach(gallery => mount(gallery, on => {
      const cards = [...gallery.querySelectorAll('[data-zs-photo]')];
      const dialog = gallery.querySelector('dialog');
      const photo = gallery.querySelector('[data-zs-large]');
      const stage = gallery.querySelector('[data-zs-image-stage]');
      const caption = gallery.querySelector('[data-zs-caption]');
      const error = gallery.querySelector('[data-zs-error]');
      const zoom = gallery.querySelector('[data-zf-zoom]');
      const filters = gallery.querySelector('[data-zs-filters]');
      const buttons = [...gallery.querySelectorAll('[data-zs-filter]')];
      const select = gallery.querySelector('[data-zm-filter]');
      const note = gallery.querySelector('[data-zf-collection-note]');
      const count = gallery.querySelector('[data-zs-count]');
      const matches = (card, category) => category === 'all' || (category === 'design' ? card.dataset.category !== 'stories' : card.dataset.category === category);
      let visible = cards, index = 0, opener = null, touch = null, zoomed = false;
      if (filters && cards.length > 1) filters.hidden = false;
      function applyFilter(category, updateUrl = false) {
        if (!buttons.some(b => b.dataset.zsFilter === category && !b.hidden)) category = 'all';
        buttons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.zsFilter === category)));
        cards.forEach(c => { c.hidden = !matches(c, category); });
        visible = cards.filter(c => !c.hidden);
        gallery.dataset.activeFilter = category;
        if (select) select.value = category;
        if (count) count.textContent = `${visible.length} ${visible.length === 1 ? 'image' : 'images'}`;
        if (note) note.textContent = category === 'stories'
          ? 'Illustrated brand stories and campaign concepts, not production specifications. Any printed offers require separate confirmation from ZILOX.'
          : category === 'all'
            ? 'Concept and editorial artwork, not production photographs or confirmed specifications. Printed offers require separate confirmation from ZILOX.'
            : 'Concept artwork and design studies, not production photographs. Final components and fitment may differ.';
        if (updateUrl) {
          try {
            const url = new URL(location.href); url.searchParams.set('view', category);
            history.replaceState(history.state, '', url);
          } catch (_) { /* Filters also work without History API access. */ }
        }
      }
      buttons.forEach(button => {
        const category = button.dataset.zsFilter;
        const n = cards.filter(c => matches(c, category)).length;
        button.hidden = n === 0;
        const badge = button.querySelector('[data-zf-filter-count]');
        if (badge) badge.textContent = String(n);
        on(button, 'click', () => applyFilter(category, true));
      });
      const initial = new URLSearchParams(location.search).get('view') || gallery.dataset.zfDefault || 'all';
      applyFilter(initial);
      on(window, 'popstate', () => applyFilter(new URLSearchParams(location.search).get('view') || gallery.dataset.zfDefault || 'all'));
      function setZoom(value) {
        zoomed = !!value;
        stage?.classList.toggle('zf-zoomed', zoomed);
        if (zoom) { zoom.setAttribute('aria-pressed', String(zoomed)); zoom.textContent = zoomed ? 'Fit image' : 'Zoom in'; }
        if (stage) {
          stage.scrollTop = 0; stage.scrollLeft = 0;
          stage.setAttribute('aria-label', zoomed ? 'Enlarged artwork. Scroll to inspect the image.' : 'Artwork viewing area');
          if (zoomed) {
            stage.style.setProperty('--zf-zoom-width', `${Math.max(photo.naturalWidth, stage.clientWidth * 1.8)}px`);
            stage.setAttribute('tabindex', '0');
          } else { stage.removeAttribute('tabindex'); stage.style.removeProperty('--zf-zoom-width'); }
        }
      }
      function busy(value) {
        stage?.setAttribute('aria-busy', String(value));
        if (zoom) zoom.disabled = value;
      }
      function show() {
        const card = visible[index]; if (!card || !photo) return;
        const a = card.querySelector('[data-zs-open]');
        setZoom(false);
        if (error) error.hidden = true;
        stage?.classList.remove('zf-image-failed');
        busy(true); photo.alt = card.querySelector('img')?.alt || '';
        // TextContent collapses the adjacent title/note; join their separate fields explicitly.
        const c = card.querySelector('figcaption');
        caption.textContent = c?.querySelector('.zf-image-title')
          ? [...c.children].map(x => x.textContent.trim()).filter(Boolean).join(' — ')
          : c?.textContent.trim() || '';
        gallery.querySelector('[data-zs-position]').textContent = `${index + 1} / ${visible.length}`;
        gallery.querySelector('[data-zs-original]').href = a.href;
        gallery.querySelectorAll('[data-zs-step]').forEach(b => { b.hidden = visible.length < 2; });
        photo.src = a.href;
      }
      function move(n) { if (visible.length) { index = (index + n + visible.length) % visible.length; show(); } }
      if (dialog && photo && typeof dialog.showModal === 'function') {
        cards.forEach(card => {
          const a = card.querySelector('[data-zs-open]'); a.setAttribute('aria-haspopup', 'dialog');
          on(a, 'click', e => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault(); opener = a; index = visible.indexOf(card); if (index < 0) return;
            show(); dialog.showModal(); document.documentElement.classList.add('zf-viewer-open');
            gallery.querySelector('[data-zs-close]').focus();
          });
        });
        on(zoom, 'click', () => { if (!zoom.disabled) setZoom(!zoomed); });
        on(gallery.querySelector('[data-zs-close]'), 'click', () => dialog.close());
        gallery.querySelectorAll('[data-zs-step]').forEach(b => on(b, 'click', () => move(Number(b.dataset.zsStep))));
        on(gallery.querySelector('[data-zs-retry]'), 'click', () => {
          if (!visible[index]) return;
          const url = new URL(visible[index].querySelector('[data-zs-open]').href);
          url.searchParams.set('_image_retry', String(Date.now()));
          error.hidden = true; stage?.classList.remove('zf-image-failed'); busy(true); photo.src = url.href;
        });
        on(dialog, 'keydown', e => {
          if (!zoomed && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
            e.preventDefault(); move(e.key === 'ArrowRight' ? 1 : -1);
          }
        });
        on(dialog, 'click', e => {
          if (e.target !== dialog) return;
          const r = dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
        });
        on(dialog, 'close', () => {
          photo.removeAttribute('src'); busy(false); setZoom(false);
          document.documentElement.classList.remove('zf-viewer-open');
          if (opener?.isConnected && !opener.closest('[hidden]')) opener.focus({preventScroll:true});
        });
        on(photo, 'error', () => {
          if (!photo.hasAttribute('src')) return;
          busy(false); stage?.classList.add('zf-image-failed');
          if (zoom) zoom.disabled = true;
          if (error) error.hidden = false;
        });
        on(photo, 'load', () => {
          if (!photo.hasAttribute('src')) return;
          busy(false); stage?.classList.remove('zf-image-failed'); if (error) error.hidden = true;
        });
        on(photo, 'touchstart', e => { touch = !zoomed && e.touches.length === 1 ? {x:e.touches[0].clientX,y:e.touches[0].clientY} : null; }, {passive:true});
        on(photo, 'touchmove', e => { if (e.touches.length !== 1 || zoomed) touch = null; }, {passive:true});
        on(photo, 'touchcancel', () => { touch = null; }, {passive:true});
        on(photo, 'touchend', e => {
          if (!zoomed && touch && e.changedTouches.length === 1 && e.touches.length === 0 && (!window.visualViewport || window.visualViewport.scale <= 1.05)) {
            const dx=e.changedTouches[0].clientX-touch.x, dy=e.changedTouches[0].clientY-touch.y;
            if (Math.abs(dx)>60 && Math.abs(dx)>Math.abs(dy)*1.7) move(dx<0 ? 1 : -1);
          }
          touch = null;
        }, {passive:true});
      }
      const videos = [...gallery.querySelectorAll('video')];
      videos.forEach(v => on(v, 'play', () => videos.forEach(other => { if (other !== v) other.pause(); })));
      on(document, 'visibilitychange', () => { if (document.hidden) videos.forEach(v => v.pause()); });
      return () => {
        if (dialog?.open) dialog.close();
        document.documentElement.classList.remove('zf-viewer-open');
        videos.forEach(v => v.pause());
      };
    }));
    all(root, '[data-zs-topic]').forEach(select => mount(select, on => {
      const requested = new URLSearchParams(location.search).get('topic');
      if ([...select.options].some(option => option.value === requested)) select.value = requested;
      const hint = select.form?.querySelector('[data-zs-topic-hint]');
      const hints = {
        'Motorcycle enquiry':'Tell us what you would like to know about the APEX RS1.',
        'Parts and rider gear':'Include the part, your bike make and model, and any fitment details.',
        'Trade enquiry':'Include your business name and the products you are interested in.',
        'Existing order support':'Include your order reference and a brief description. Do not send payment-card details.',
        'Other question':'Tell us how we can help. Please do not include sensitive financial information.'
      };
      const update = () => { if (hint) hint.textContent = hints[select.value] || hints['Other question']; };
      update(); on(select,'change',update);
    }));
    all(root,'[data-zs-feedback]').forEach(el => el.focus({preventScroll:false}));
  }
  document.addEventListener('shopify:section:load', e => refresh(e.target));
  document.addEventListener('shopify:section:unload', e => controllers.forEach((dispose,el) => { if (e.target.contains(el)) { dispose(); controllers.delete(el); } }));
  window.ZiloxStudio = {refresh}; refresh();
})();
